<template>
  <header 
    :class="[
      'fixed top-0 w-full py-3 md:py-4 transition-all duration-500 ease-in-out', 
      navBackgroundClass
    ]"
    style="z-index: 50;"
  >
    <div class="container-fluid mx-auto px-4 md:px-8 lg:px-16">
      <div class="flex items-center justify-between">
        <!-- Logo and site identity -->
        <div class="flex items-center">
          <a href="/" class="flex items-center" @click.prevent="scrollToSection('hero')">
            <img src="../assets/st.png" alt="Sherborne Therapy" class="h-10 w-auto" />
            <span 
              class="ml-2 text-lg font-semibold transition-all duration-300" 
              :class="[
                nameVisibilityClass,
                scrolled ? 'text-site-text' : 'text-white'
              ]"
            >
              Sherborne Therapy
            </span>
          </a>
        </div>
        
        <!-- Navigation items for desktop -->
        <nav class="hidden md:flex items-center space-x-1">
          <button 
            v-for="item in navItems" 
            :key="item.id"
            class="relative px-3 py-2 text-sm font-medium rounded-md transition-colors duration-300"
            :class="[
              activeSection === item.id ? 
                (scrolled ? 'text-site-terracotta' : 'text-site-terracotta-light') : 
                (scrolled ? 'text-site-text hover:text-site-terracotta' : 'text-white hover:text-site-terracotta-light')
            ]"
            @click="scrollToSection(item.id)"
          >
            <span class="relative z-10">{{ item.name }}</span>
          </button>
        </nav>
        
        <!-- Mobile menu button -->
        <button 
          class="md:hidden p-2 transition-colors duration-300"
          :class="scrolled ? 'text-site-text' : 'text-white'"
          @click="toggleMobileMenu"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            class="h-6 w-6" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
      
      <!-- Mobile navigation menu (hidden by default) -->
      <div 
        v-show="isMobileMenuOpen" 
        class="md:hidden mt-3 bg-white/95 backdrop-blur-md rounded-lg shadow-lg overflow-hidden transition-all duration-300"
      >
        <div class="py-2 px-1">
          <div class="flex items-center px-4 py-3 border-b border-gray-100">
            <img src="../assets/st.png" alt="Sherborne Therapy" class="h-8 w-auto" />
            <span class="ml-2 text-base font-semibold text-site-text">Sherborne Therapy</span>
          </div>
          <button 
            v-for="item in navItems" 
            :key="item.id"
            class="block w-full text-left px-4 py-3 text-site-text text-sm font-medium transition-colors duration-300"
            :class="activeSection === item.id ? 'bg-site-offwhite text-site-terracotta' : 'hover:bg-site-offwhite'"
            @click="scrollToSection(item.id); closeMobileMenu()"
          >
            {{ item.name }}
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { gsap } from '@gsap/shockingly'
import { ScrollTrigger } from '@gsap/shockingly/ScrollTrigger'
import { ScrollToPlugin } from '@gsap/shockingly/ScrollToPlugin'

export default {
  name: 'NavBar',
  data() {
    return {
      scrolled: false,
      videoProgress: 0,
      activeSection: 'hero',
      isMobileMenuOpen: false,
      navItems: [
        { id: 'what-i-offer', name: 'What I Offer' },
        { id: 'about', name: 'About' },
        { id: 'qualifications', name: 'Qualifications' },
        { id: 'sessions', name: 'Sessions' },
        { id: 'therapy-room', name: 'Therapy Room' },
        { id: 'contact', name: 'Contact' }
      ]
    }
  },
  computed: {
    navBackgroundClass() {
      if (this.activeSection === 'hero' && !this.scrolled) {
        return 'bg-transparent'
      } else if (this.scrolled) {
        return 'bg-white/90 backdrop-blur-md shadow-sm'
      } else {
        // Default semi-transparent background for other sections
        if (this.activeSection === 'about') {
          return 'bg-site-cream/80 backdrop-blur-md shadow-sm'
        } else if (this.activeSection === 'therapy-room') {
          return 'bg-site-sand/80 backdrop-blur-md shadow-sm'
        } else if (this.activeSection === 'what-i-offer') {
          return 'bg-site-dusty-rose/80 backdrop-blur-md shadow-sm'
        } else {
          return 'bg-white/80 backdrop-blur-md shadow-sm'
        }
      }
    },
    nameVisibilityClass() {
      // Only show the name when scrolled or not in hero section
      if (this.activeSection !== 'hero' || this.videoProgress > 0.8 || this.scrolled) {
        return 'opacity-100 md:block'
      } else {
        return 'opacity-0 md:block'
      }
    }
  },
  mounted() {
    // Register GSAP plugins
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)
    
    // Set up scroll event listener
    window.addEventListener('scroll', this.handleScroll)
    
    // Immediately check scroll position to set initial state
    this.handleScroll()
    
    // Set up ScrollTrigger for each section
    this.setupSectionTracking()
    
    // Listen for video progress updates from HeroSection
    window.addEventListener('videoProgressUpdate', this.handleVideoProgress)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('videoProgressUpdate', this.handleVideoProgress)
  },
  methods: {
    handleScroll() {
      this.scrolled = window.scrollY > 50
    },
    setupSectionTracking() {
      // Wait for the DOM to be fully ready
      this.$nextTick(() => {
        // Clear any existing ScrollTriggers
        ScrollTrigger.getAll().forEach(trigger => {
          if (trigger.vars.id && trigger.vars.id.startsWith('nav-')) {
            trigger.kill();
          }
        });
        
        // Set up ScrollTrigger for each section
        this.navItems.forEach(item => {
          const section = document.getElementById(item.id);
          if (section) {
            ScrollTrigger.create({
              id: `nav-${item.id}`,
              trigger: `#${item.id}`,
              start: 'top 50%',
              end: 'bottom 50%',
              onEnter: () => this.activeSection = item.id,
              onEnterBack: () => this.activeSection = item.id
            });
          }
        });
      });
    },
    handleVideoProgress(event) {
      this.videoProgress = event.detail.progress
    },
    scrollToSection(sectionId) {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    },
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
    },
    closeMobileMenu() {
      this.isMobileMenuOpen = false;
    }
  }
}
</script>

<style scoped>
/* Container for centered content */
.container-fluid {
  width: 100%;
  max-width: 1600px;
}

/* Fix backdrop-filter in Safari */
@supports not (backdrop-filter: blur(8px)) {
  header[class*='backdrop-blur'] {
    backdrop-filter: none;
    background-color: rgba(255, 255, 255, 0.95) !important;
  }
}

/* Glass effect */
header {
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* Mobile menu animation */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.mt-3 {
  animation: fadeIn 0.3s ease-out;
}
</style>